import type { App } from 'vue'

import type { AccountInfo } from '@azure/msal-browser'
import type { RouteLocationNormalized } from 'vue-router'
import type { MatterParameters } from './types/api'
import type { AppEnvironment, MatterInfoKeys, PredictionParameters, PredictionParametersKeys, UserModule } from './types'
import paramDefs from '~/schemas/parameters'
import type { PredictionParameterDefinitions } from '~/schemas/parameters'
import { Role } from '~/types'

/**
 * Awaitable setTimeout. Sleeps for given number of milliseconds.
 * @param ms milliseconds to wait
 */
export const sleep = async (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

// /**
//  * Installs all installable modules in the `modules` folder
//  * @param app Vue app instance
//  */
// export const installModules = (app: App) => Object.values(import.meta.glob<{ install: UserModule }>('./modules/*.ts', { eager: true })).forEach(i => i.install?.(app))

/**
 * Installs all installable modules provided
 * @param app Vue app instance
 * @param modules Array of modules to install
 */
export function installModules(app: App, modules: { install: UserModule }[]) {
  modules.forEach(module => module.install(app))
}
/**
 * The current environment the app is running in
 */
export const appEnvironment = (import.meta.env.MODE as AppEnvironment)

/**
 * Whether the app is running in development mode
 */
export const isOfflineDevelopment = (import.meta.env.MODE as AppEnvironment) === 'offline-development'

/**
 * Goes back in the browser history
 */
export function goBack() {
  window.history.back()
}

/**
 * Replaces all values inside an object with `undefined`
 * @param obj any object
 * @returns object with all values replaced with `undefined`
 */
export const emptyObject = <TObj extends Record<string, any>>(obj: TObj) => Object.entries(obj).reduce((acc, [key]) => ({ ...acc, [key]: undefined }), {} as { [K in keyof TObj]: undefined })

/**
 * Serializes a boolean value to a string for use in stored proc
 */
export const serializeBoolean = (value?: boolean, trueValue = 'yes', falseValue = 'no') => value !== undefined ? value === true ? trueValue : falseValue : undefined

/**
 * Maps a stored proc string value to a boolean for use inside the application fields
 * @param value
 * @param trueValue
 * @returns
 */
export function mapBoolean(value?: 'yes' | 'no' | undefined, trueValue = 'yes') {
  if (value === trueValue)
    return true
  else if (value !== undefined)
    return false
  return undefined
}

/**
 * Formats a number as a currency string
 */
export const formatCurrency = (value: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(value)

/**
 * Formats a number as a string with 2 decimal places
 */
export const formatNumber = (value: number) => new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value)

/**
 * Formats a number with a unit
 * @param value number to format
 * @param unit unit to append/prepend to the number
 * @param unitOrder whether to prepend or append the unit
 */
export function formatUnit(value?: number, unit?: string, unitOrder: 'before' | 'after' = 'after') {
  if (!value)
    return undefined
  if (unit === '£')
    return formatCurrency(value)

  else if (unit === undefined)
    unit = ''

  const prepend = unitOrder === 'before' ? unit : ''
  const append = unitOrder === 'after' ? ` ${unit}` : ''

  return (`${prepend}${formatNumber(value)}${append}`).trim()
}

/**
 * Returns the ordinal suffix for given number. Can be `st`, `nd`, `rd` or `th`
 * @param n number to get the ordinal suffix for
 */
export function getOrdinalSuffix(n?: number) {
  if (!n)
    return
  const pr = new Intl.PluralRules('en-GB', { type: 'ordinal' })

  const suffixes = new Map([
    ['one', 'st'],
    ['two', 'nd'],
    ['few', 'rd'],
    ['other', 'th'],
  ])

  const rule = pr.select(n)

  return suffixes.get(rule) as 'st' | 'nd' | 'rd' | 'th'
}

/**
 * Gets the display name of the option
 * @param paramName
 * @param value
 * @returns
 */
export function resolveOption(paramName: keyof MatterParameters, value: string | null | undefined) {
  const definition = paramDefs.find(x => x.name === paramName)!

  if (definition.type === 'number') {
    return value
  }
  else if (definition.type === 'switch') {
    if (definition.options.start.value === value)
      return definition.options.start.name
    else if (definition.options.end.value === value)
      return definition.options.end.name
    else return value
  }
  else if (definition.type === 'select') {
    if (!Array.isArray(definition.options))
      return
    const option = definition.options.find(x => x.value === value)
    if (option)
      return option.name
    else return value
  }
  else { return value }
}

/**
 * Returns a new object with the given keys omitted
 * @param obj object to omit keys from
 * @param keys keys to omit
 */
export function objectOmit<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]) {
  const newObj = { ...obj }
  keys.forEach(key => delete newObj[key])
  return newObj as Omit<T, K>
}

export const predictionParamsKeys = paramDefs.map(param => param.name) satisfies PredictionParametersKeys

export const predictionParamsDefaults = (paramDefs as PredictionParameterDefinitions)
  .reduce((acc, param) => ({ ...acc, ...(param.defaultValue !== undefined ? { [param.name]: param.defaultValue } : {}) }), {}) as PredictionParameters

export const matterInfoKeys = [
  'caseHandler',
  'clientName',
  'clientReference',
  'dateInstructionsReceived',
  'dateOfAccident',
  'insurerHandler',
  'matterDescription',
  'weightmansReference',
] satisfies MatterInfoKeys

export const dateFormat = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

export const dateTimeFormat = new Intl.DateTimeFormat('en', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
})

export function formatDetails(details?: Record<string, string | number | null | undefined>) {
  if (!details)
    return undefined
  return Object.entries(details).map(([key, val]) => {
    return (val !== null && val !== undefined) ? `${key}: ${val}` : undefined
  }).filter(val => !!val).join(' | ')
}

export function getDetailNameValues<T extends string>(names: Record<T, string>, values?: Record<string, string | number | null | undefined>) {
  return Object.entries(names || {}).reduce((acc, [key]) => ({
    ...acc,
    [names[key as T]]: values?.[key as T],
  }), {})
}

export function getDetailNames(paramDefs: PredictionParameterDefinitions) {
  return paramDefs.reduce((acc, val) => ({
    ...acc,
    [val.name]: val.label,
  }), {} as Record<typeof paramDefs[number]['name'], string>)
}

/**
 * Gets typed keys array of an object
 */
export const keys = <T extends Readonly<Record<string, any>>>(obj: T) => Object.keys(obj) as (keyof T)[]

/**
 * Gets basic state parameters without having to wait for modules to load. Useful for usage before the app is fully loaded
 * @param to route the user is navigating to
 */
export function getImmediateState(to?: RouteLocationNormalized) {
  const isMatterId = !!(localStorage.getItem('matterReferenceId') || to?.query?.matterReferenceId?.toString())

  const accountKeys = JSON.parse(localStorage.getItem('msal.account.keys') || '[]') as string[]

  const roles = getRolesFromLocalStorage()

  const account = JSON.parse(localStorage.getItem(accountKeys?.[0]) || 'null') as AccountInfo | null

  const isSignedIn = !!account

  const isAuthorized = !!roles?.includes(Role.Handler)

  return {
    isMatterId,
    isSignedIn,
    isAuthorized,
  }
}

function getRolesFromLocalStorage() {
  let index = 0
  while (localStorage.key(index) !== null) {
    const key = localStorage.key(index)
    if (key?.includes('idtoken')) {
      const token = JSON.parse(localStorage.getItem(key) ?? 'null')
      if (token) {
        const decoded = parseJwt(token.secret)
        if (decoded?.roles)
          return decoded.roles as string[]
      }
    }
    index++
  }
}

function parseJwt(token: string) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(window.atob(base64).split('').map((c) => {
    return `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`
  }).join(''))

  return JSON.parse(jsonPayload)
}
